import { useEffect, useRef, useState } from 'react';
import { useToast } from '@components/Toast';
import { api, ApiError } from '@/lib/api';

/**
 * The legal-aid request panel. Deliberately short: someone asking for help with
 * a case in progress should not have to fill in a form the length of a claim.
 *
 * What people write here describes a live legal matter, often involving someone
 * in detention, so it is treated as sensitive from the moment it is typed.
 */

interface FormState {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    description: string;
}

const EMPTY: FormState = { firstName: '', lastName: '', email: '', phone: '', description: '' };

const FIELD =
    'h-[46px] w-full rounded-[8px] border border-[#e2e2e2] bg-white px-4 text-[14px] text-[#151515] outline-none transition-colors placeholder:text-[#9a9a9a] focus:border-[#f60]';

function validate(form: FormState): Partial<Record<keyof FormState, string>> {
    const errors: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) errors.firstName = 'Tell us your first name.';
    if (!form.lastName.trim()) errors.lastName = 'Tell us your last name.';
    if (!form.email.trim() && !form.phone.trim())
        errors.email = 'We need either an email or a phone number to reach you.';
    else if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
        errors.email = 'That email does not look right.';
    if (form.phone.trim() && !/^[+\d][\d\s()-]{6,}$/.test(form.phone.trim()))
        errors.phone = 'That phone number does not look right.';
    if (!form.description.trim()) errors.description = 'Tell us what happened, in your own words.';
    return errors;
}

export default function LegalAidForm({ open, onClose }: { open: boolean; onClose: () => void }) {
    const toast = useToast();
    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
    const [sending, setSending] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        const scroller = document.querySelector<HTMLElement>('.page-scroll-root');
        const prev = scroller?.style.overflowY ?? '';
        if (scroller) scroller.style.overflowY = 'hidden';
        panelRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            if (scroller) scroller.style.overflowY = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const found = validate(form);
        setErrors(found);

        if (Object.keys(found).length) {
            const n = Object.keys(found).length;
            toast.error(`${n} thing${n === 1 ? '' : 's'} still needed`, 'Check the highlighted fields and send again.');
            return;
        }

        setSending(true);
        try {
            await api.post('/submissions/legal-aid', {
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                description: form.description.trim(),
            });
            toast.success(
                'Request received',
                'A caseworker will read this and contact you. If someone is in custody, say so when they call.',
            );
            setForm(EMPTY);
            onClose();
        } catch (error) {
            if (error instanceof ApiError && error.fields) {
                setErrors(error.fields);
                toast.error('Some fields need attention', 'Check the highlighted fields and send again.');
            } else {
                toast.error(
                    'That did not send',
                    error instanceof ApiError && error.status === 0
                        ? 'We could not reach the server. Try again in a moment.'
                        : 'Something went wrong on our side. Try again in a moment.',
                );
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[3500]">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 animate-[fade-in_220ms_ease-out] bg-black/55 backdrop-blur-[2px]"
            />

            <div
                ref={panelRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="legal-aid-form-title"
                className="absolute inset-y-0 right-0 flex w-[min(520px,100vw)] animate-[slide-in-right_320ms_cubic-bezier(0.22,1,0.36,1)] flex-col bg-white shadow-[-24px_0_60px_-20px_rgba(0,0,0,0.4)] outline-none"
            >
                <div className="flex items-start justify-between gap-4 border-b border-[#e2e2e2] px-6 py-5 lg:px-8">
                    <div>
                        <h2
                            id="legal-aid-form-title"
                            className="font-primary text-[24px] font-bold leading-[1.2] tracking-[-0.8px] text-[#08080c] lg:text-[28px]"
                        >
                            Request legal aid
                        </h2>
                        <p className="mt-1 text-[13px] leading-[1.6] text-[#5e5e5e]">
                            Tell us what happened. There is no fee, and you do not need a referral.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close form"
                        className="-mr-2 flex size-9 shrink-0 items-center justify-center rounded-full text-[#5e5e5e] transition-colors hover:bg-[#f0f0f0] hover:text-[#08080c]"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                                d="M6 6l12 12M18 6L6 18"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
                    <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-8">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <Labelled label="First name" error={errors.firstName} required>
                                <input
                                    className={FIELD}
                                    value={form.firstName}
                                    onChange={(e) => set('firstName', e.target.value)}
                                    placeholder="Ada"
                                    autoComplete="given-name"
                                />
                            </Labelled>
                            <Labelled label="Last name" error={errors.lastName} required>
                                <input
                                    className={FIELD}
                                    value={form.lastName}
                                    onChange={(e) => set('lastName', e.target.value)}
                                    placeholder="Okafor"
                                    autoComplete="family-name"
                                />
                            </Labelled>
                        </div>

                        <div className="mt-5 grid gap-5 sm:grid-cols-2">
                            <Labelled label="Email" error={errors.email} hint="Either one">
                                <input
                                    type="email"
                                    className={FIELD}
                                    value={form.email}
                                    onChange={(e) => set('email', e.target.value)}
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                />
                            </Labelled>
                            <Labelled label="Phone number" error={errors.phone} hint="Either one">
                                <input
                                    type="tel"
                                    className={FIELD}
                                    value={form.phone}
                                    onChange={(e) => set('phone', e.target.value)}
                                    placeholder="+234 800 000 0000"
                                    autoComplete="tel"
                                />
                            </Labelled>
                        </div>

                        <div className="mt-6">
                            <Labelled
                                label="What happened?"
                                error={errors.description}
                                required
                                hint="As much or as little as you can"
                            >
                                <textarea
                                    rows={7}
                                    className="w-full resize-y rounded-[8px] border border-[#e2e2e2] bg-white px-4 py-3 text-[14px] leading-[1.7] text-[#151515] outline-none transition-colors placeholder:text-[#9a9a9a] focus:border-[#f60]"
                                    value={form.description}
                                    onChange={(e) => set('description', e.target.value)}
                                    placeholder="When it started, who is involved, whether anyone is in custody, and what has happened so far."
                                />
                            </Labelled>
                        </div>

                        <p className="mt-4 rounded-[8px] bg-[#fff4ec] px-4 py-3 text-[12px] leading-[1.7] text-[#8a4b00]">
                            If someone is in custody right now, say so above — those requests are read first.
                        </p>
                    </div>

                    <div className="border-t border-[#e2e2e2] px-6 py-5 lg:px-8">
                        <button
                            type="submit"
                            disabled={sending}
                            className="flex h-[48px] w-full items-center justify-center rounded-[10px] bg-[#f60] text-[15px] font-bold text-white transition-transform duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {sending ? 'Sending…' : 'Send my request'}
                        </button>
                        <p className="mt-3 text-center text-[11px] leading-[1.6] text-[#9a9a9a]">
                            What you write here is read only by our legal team.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Labelled({
    label,
    hint,
    error,
    required,
    children,
}: {
    label: string;
    hint?: string;
    error?: string;
    required?: boolean;
    children: React.ReactNode;
}) {
    return (
        <label className="block">
            <span className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-bold text-[#08080c]">
                    {label} {required && <span className="text-[#f60]">*</span>}
                </span>
                {hint && <span className="text-[11px] text-[#9a9a9a]">{hint}</span>}
            </span>
            <span className="mt-2 block">{children}</span>
            {error && <span className="mt-1.5 block text-[12px] text-[#d33]">{error}</span>}
        </label>
    );
}
